import { useEffect, useState } from 'react';
import { components } from 'react-select';
import api from 'api';
import { useReduxState } from 'hooks';
import { User } from 'components/common';
import { AdminGroupAvatar } from 'components/common/admins';
import { Select } from 'components/common/form';

import type { AdminGroup, User as UserProps } from 'shared-types';

export type AdminFromSelect = {
  adminGroup?: AdminGroup;
  adminGroupId?: number;
  user?: UserProps;
  userId?: number;
};

interface Props {
  error?: string | undefined;
  existingAdminGroupIds?: number[];
  existingUserIds?: number[];
  field?: string;
  id?: string;
  isClearable?: boolean;
  isMulti?: boolean;
  includeAdminGroups?: boolean;
  isRequired?: boolean;
  nonAdminsWithEmailOnly?: boolean;
  onInputChange?: () => void;
  placeholder?: string;
  searchParams: string;
}

const SelectAdmin = ({
  error,
  existingAdminGroupIds,
  existingUserIds,
  field = 'userId',
  id = 'select-admin',
  includeAdminGroups,
  isClearable = true,
  isMulti = false,
  isRequired = true,
  nonAdminsWithEmailOnly,
  onInputChange,
  placeholder,
  searchParams
}: Props) => {
  const { messages } = useReduxState();

  // use these to track added ids when using isMulti because react-select
  // can't distinguish between different values in this case
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [selectedAdminGroupIds, setSelectedAdminGroupIds] = useState([]);

  let getUsersTimeout: NodeJS.Timeout;

  useEffect(() => {
    return () => clearTimeout(getUsersTimeout);
  }, []);

  const getUserOptions = async (searchString: string) => {
    clearTimeout(getUsersTimeout);
    const userOptions: {
      label: string;
      value: {
        adminGroup?: AdminGroup;
        adminGroupId?: number;
        user?: UserProps;
        userId?: number;
      };
    }[] = await new Promise((resolve) => {
      getUsersTimeout = setTimeout(async () => {
        const users = [];

        try {
          const response = await api.get(
            `/admin/users?search=${searchString}${searchParams}`
          );

          if (response.data && response.data.success) {
            users.push(
              ...response.data.users.map((user: UserProps) => ({
                label: user.email || user.username,
                value: {
                  userId: user.id,
                  user,
                  // use this to ensure a unique key value in react-select MultiValue
                  toString: () => `user-id-${user.id}`
                }
              }))
            );
          }
        } catch (err) {}

        if (includeAdminGroups) {
          try {
            const response = await api.get(
              `/admin/admin-groups?search=${searchString}`
            );

            if (response.data && response.data.success) {
              users.push(
                ...response.data.adminGroups.map((adminGroup) => ({
                  value: {
                    adminGroupId: adminGroup.id,
                    adminGroup,
                    // use this to ensure a unique key value in react-select MultiValue
                    toString: () => `admin-group-id-${adminGroup.id}`
                  },
                  label: adminGroup.title
                }))
              );
            }
          } catch (err) {}
        }

        resolve(users);
      }, 500);
    });

    return userOptions.filter((user) => {
      let isValid = true;

      if (
        user.value.hasOwnProperty('userId') &&
        (existingUserIds?.includes(user.value.userId) ||
          selectedUserIds.includes(user.value.userId))
      ) {
        isValid = false;
      }

      if (
        user.value.hasOwnProperty('userId') &&
        nonAdminsWithEmailOnly &&
        (user.value.user?.roles?.includes('admin') || !user.value.user?.email)
      ) {
        isValid = false;
      }

      if (
        user.value.hasOwnProperty('adminGroupId') &&
        (existingAdminGroupIds?.includes(user.value.adminGroupId) ||
          selectedAdminGroupIds.includes(user.value.adminGroupId))
      ) {
        isValid = false;
      }

      return isValid;
    });
  };

  const handleChange = (value) => {
    if (isMulti && Array.isArray(value)) {
      setSelectedUserIds(
        value?.filter((item) => item.userId).map((item) => item.userId) || []
      );
      setSelectedAdminGroupIds(
        value
          ?.filter((item) => item.adminGroupId)
          .map((item) => item.adminGroupId) || []
      );
    }
  };

  const validateRequired = (value) => {
    return value ? undefined : messages.commonFieldRequired;
  };

  const Option = (props) => {
    return (
      <components.Option {...props}>
        {props.value?.adminGroup ? (
          <AdminGroupAvatar
            adminGroup={props.value.adminGroup}
            enableLink={false}
            hideLock={true}
          />
        ) : props.value?.user ? (
          <User user={props.value.user} />
        ) : null}
      </components.Option>
    );
  };

  return (
    <div className="select-admin">
      <Select
        async={true}
        components={{ Option }}
        error={error}
        field={field}
        id={id}
        isClearable={isClearable}
        isMulti={isMulti}
        isSearchable={true}
        loadingMessage={messages.settingsSearching}
        loadOptions={getUserOptions}
        noOptionsMessage={messages.settingsNoUsersFound}
        onChange={handleChange}
        onInputChange={onInputChange}
        placeholder={
          placeholder ||
          `${messages.settingsSearchForUser} (${messages.usersAdminUsers})`
        }
        showLabelForSelectedElement={true}
        {...(isRequired
          ? { validate: validateRequired, validateOnChange: true }
          : {})}
      />
    </div>
  );
};

export default SelectAdmin;
